import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, Button } from '@/components';
import { useShopProfile, useUpdateShopProfile, useShopLogoUrl, useEntitlement } from '@/hooks/useProApi';
import { utcToLocal, type ShopProfileUpdate } from '@/types';
import { useTheme, type ThemeChoice } from '@/theme';

export function Settings() {
  const { user } = useAuth0();
  const { data: entitlement } = useEntitlement();
  const { choice: themeChoice, setChoice: setThemeChoice, resolved: themeOnScreen } = useTheme();
  const { data: shopProfile, isLoading } = useShopProfile();
  const { data: logoUrl } = useShopLogoUrl();
  const updateProfile = useUpdateShopProfile();

  const [formData, setFormData] = useState<ShopProfileUpdate>({
    shopName: '',
    shopPhone: '',
    shopEmail: '',
    shopAddress: '',
    logoBase64: null,
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form when profile loads
  useEffect(() => {
    if (shopProfile) {
      setFormData({
        shopName: shopProfile.shopName || '',
        shopPhone: shopProfile.shopPhone || '',
        shopEmail: shopProfile.shopEmail || '',
        shopAddress: shopProfile.shopAddress || '',
        logoBase64: null,
      });
    }
  }, [shopProfile]);

  // Set logo preview from existing logo URL
  useEffect(() => {
    if (logoUrl?.logoUrl && !logoPreview) {
      setLogoPreview(logoUrl.logoUrl);
    }
  }, [logoUrl, logoPreview]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image must be less than 2MB');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        setFormData((prev) => ({ ...prev, logoBase64: base64 }));
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(formData);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData((prev) => ({ ...prev, logoBase64: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-obdai-gradient-start"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400 mt-1">Manage your shop profile and branding</p>
      </div>

      {/* Shop Profile Form */}
      <Card title="Shop Profile">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Shop Logo
            </label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 bg-obdai-deep rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-600">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Shop logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg cursor-pointer hover:bg-gray-600 transition"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload Logo
                </label>
                {logoPreview && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="ml-2 text-red-400 hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  Recommended: Square image, PNG or JPG, max 2MB
                </p>
              </div>
            </div>
          </div>

          {/* Shop Name */}
          <div>
            <label htmlFor="shopName" className="block text-sm font-medium text-gray-300 mb-2">
              Shop Name
            </label>
            <input
              type="text"
              id="shopName"
              name="shopName"
              value={formData.shopName || ''}
              onChange={handleInputChange}
              placeholder="Your Auto Shop"
              className="w-full px-4 py-3 bg-obdai-deep border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-obdai-gradient-start"
            />
          </div>

          {/* Shop Phone */}
          <div>
            <label htmlFor="shopPhone" className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="shopPhone"
              name="shopPhone"
              value={formData.shopPhone || ''}
              onChange={handleInputChange}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 bg-obdai-deep border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-obdai-gradient-start"
            />
          </div>

          {/* Shop Email */}
          <div>
            <label htmlFor="shopEmail" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="shopEmail"
              name="shopEmail"
              value={formData.shopEmail || ''}
              onChange={handleInputChange}
              placeholder="contact@yourshop.com"
              className="w-full px-4 py-3 bg-obdai-deep border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-obdai-gradient-start"
            />
          </div>

          {/* Shop Address */}
          <div>
            <label htmlFor="shopAddress" className="block text-sm font-medium text-gray-300 mb-2">
              Address
            </label>
            <textarea
              id="shopAddress"
              name="shopAddress"
              value={formData.shopAddress || ''}
              onChange={handleInputChange}
              placeholder="123 Main Street, City, State 12345"
              rows={2}
              className="w-full px-4 py-3 bg-obdai-deep border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-obdai-gradient-start resize-none"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <Button
              type="submit"
              isLoading={updateProfile.isPending}
            >
              Save Changes
            </Button>
          </div>

          {updateProfile.isSuccess && (
            <p className="text-green-400 text-sm text-center">Profile updated successfully!</p>
          )}
          {updateProfile.isError && (
            <p className="text-red-400 text-sm text-center">Failed to update profile. Please try again.</p>
          )}
        </form>
      </Card>

      {/* Branding Preview */}
      <Card title="Report Branding Preview">
        <div className="bg-white rounded-lg p-6 text-gray-900">
          <div className="flex items-center gap-4 border-b pb-4 mb-4">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-16 h-16 object-contain" />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                <span className="text-gray-400 text-xs">Logo</span>
              </div>
            )}
            <div>
              <h3 className="font-bold text-lg">
                {formData.shopName || 'Your Shop Name'}
              </h3>
              {formData.shopPhone && <p className="text-sm">{formData.shopPhone}</p>}
              {formData.shopEmail && <p className="text-sm">{formData.shopEmail}</p>}
              {formData.shopAddress && <p className="text-sm">{formData.shopAddress}</p>}
            </div>
          </div>
          <div className="text-center text-gray-500 text-sm">
            <p>This is how your branding will appear on generated reports</p>
          </div>
        </div>
      </Card>

      {/* Appearance */}
      <Card title="Appearance">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white font-medium">Theme</p>
            <p className="text-gray-400 text-sm">
              {themeChoice === 'system'
                ? `Following this device, which is set to ${themeOnScreen} right now.`
                : 'Saved on this browser. Reports and PDFs are not affected.'}
            </p>
          </div>
          <div className="inline-flex p-1 rounded-xl bg-black/20 border border-white/10" role="radiogroup" aria-label="Theme">
            {([
              { id: 'dark', label: 'Dark', icon: 'M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z' },
              { id: 'light', label: 'Light', icon: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z' },
              { id: 'system', label: 'Match device', icon: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25' },
            ] as { id: ThemeChoice; label: string; icon: string }[]).map(opt => {
              const active = themeChoice === opt.id;
              return (
                <button
                  key={opt.id}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setThemeChoice(opt.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    active ? 'bg-obdai-gradient text-white shadow' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={opt.icon} />
                  </svg>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Account Info */}
      <Card title="Account">
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-700">
            <span className="text-gray-400">Email</span>
            <span className="text-white">{user?.email || entitlement?.email || '-'}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-700">
            <span className="text-gray-400">Plan</span>
            <span className="text-white font-medium">
              {entitlement?.plan?.toUpperCase() || 'FREE'}
            </span>
          </div>
          {entitlement?.expiresAt && (
            <div className="flex justify-between items-center py-3 border-b border-gray-700">
              <span className="text-gray-400">Expires</span>
              <span className="text-white">
                {format(utcToLocal(entitlement.expiresAt), 'MMMM d, yyyy')}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center py-3">
            <span className="text-gray-400">User ID</span>
            <span className="text-gray-500 text-xs font-mono">{user?.email || entitlement?.email || '-'}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
